import {useCurrentCompany} from "@/api/companyApi";
import {useIsOwnProfile, useSelectedAgency} from "@/hooks/useUserHooks";
import useUserStore from "@/store/user";
import {useParams} from "@tanstack/react-router";
import {useDebugValue} from "react";

const usePackageLimits = ({
  ownProfile,
  isEnabled = true,
}: {
  ownProfile?: boolean;
  isEnabled?: boolean;
} = {}) => {
  useDebugValue("usePackageLimits");
  const {agencyId: agencyIdFromUrl} = useParams({strict: false});
  const agency = useSelectedAgency();
  const loggedInUser = useUserStore(state => state.user);

  const isProfileOwnedByUser = useIsOwnProfile();
  const isOwnProfile = ownProfile || isProfileOwnedByUser;

  const {data: fetchedCompany, isLoading} = useCurrentCompany({
    ownProfile: isOwnProfile,
    isEnabled,
  });

  const companySubscription = isOwnProfile ? loggedInUser.package_subscription : fetchedCompany?.package_subscription;
  const companyContentBundles = isOwnProfile ? loggedInUser.buyouts : fetchedCompany?.buyouts;

  const totalBuyouts =
    (companySubscription?.buyout ?? 0) + (companyContentBundles?.reduce((acc, bundle) => acc + bundle.buyout!, 0) ?? 0);
  const remainingBuyouts =
    totalBuyouts -
    (companySubscription?.buyout_used ?? 0) -
    (companyContentBundles?.reduce((acc, bundle) => acc + bundle.buyout_used, 0) ?? 0);

  const returnValue = {
    credits: +((agencyIdFromUrl ? agency?.credits : fetchedCompany?.agency?.credits) ?? 0).toFixed(2),
    isCreditSystemActive: companySubscription?.is_admin_assigned,
    isAdminAssigned: !!companySubscription?.is_admin_assigned,
    isLoading,
    isOwnProfile,
    hasSubscription: !!companySubscription,
    inviteLimit: companySubscription?.invitations_per_campaign ?? 1000000,
    influencerLimit: companySubscription?.influencers_per_campaign ?? 1000000,
    keywordLimit: companySubscription?.tiktok_visibility_keyword_limit ?? 0,
    isInstagramEnabled: companySubscription?.instagram_campaigns,
    isTiktokEnabled: companySubscription?.tiktok_campaigns,
    name: companySubscription?.name,
    validUntil: companySubscription?.valid_until,
    containsMultipleCampaigns: companySubscription?.multiple_active_campaigns ?? false,
    parallelCampaignLimit: companySubscription?.number_of_multiple_active_campaigns,
    proInfluencerFee: agencyIdFromUrl
      ? agency?.pro_service_fee_percentage
      : fetchedCompany?.agency?.pro_service_fee_percentage,
    totalBuyouts,
    regularInfluencerFee: agencyIdFromUrl
      ? agency?.cash_reward_service_fee_percentage
      : fetchedCompany?.agency?.cash_reward_service_fee_percentage,
    remainingBuyouts,
    packageIncludedBuyouts: companySubscription?.buyout ?? 0,
    includesInsights: companySubscription?.influencer_level_insights ?? false,
    packageCategory: companySubscription?.package_category,
    packageType: companySubscription?.package_type,
    pricePerMonth: companySubscription?.price_per_month,
    pricePerYear: companySubscription?.price_per_year,
    currency: companySubscription?.currency ?? fetchedCompany?.currency ?? loggedInUser.currency,
    hasProCreatorMode: companySubscription?.pro_creator_mode,
    hasContentApproval: companySubscription?.content_approval,
    hasSocialFeedFilters: companySubscription?.social_feed_filters,
    hasShortTermCampaigns: companySubscription?.short_term_campaigns ?? false,
    hasPersonalLinkStickers: companySubscription?.personal_link_stickers ?? false,
    hasTiktokSearch: !!companySubscription?.tiktok_search,
    hasCampaignBooster: !!companySubscription?.campaign_booster,
  };
  return returnValue;
};

export default usePackageLimits;
